import {
  ExtendedFlexibleGallery,
  type ExtendedFlexibleGalleryType,
} from '@smart/editorial-components';
import type {GalleryItem} from '@smart/editorial-components/dist/components/extended-flexible-gallery';
import type {ExtendedFlexibleGalleryContent} from '@smart/website-aem-utils';
import {
  createLegalDisclaimerProps,
  createLinkProps,
} from '@smart/website-aem-utils';
import * as React from 'react';
import {useFeatureServices} from '../hooks/use-feature-services';
import {PageModelContext} from './page';

export interface ExtendedFlexibleGalleryWrapperProps {
  readonly content: ExtendedFlexibleGalleryContent;
}

export const ExtendedFlexibleGalleryWrapper = React.memo(
  ({content}: ExtendedFlexibleGalleryWrapperProps) => {
    const pageModel = React.useContext(PageModelContext);
    const {languageTag = ``, marketId = ``} = pageModel?.locale || {};

    const {endpointDirectory, loginService, historyService} =
      useFeatureServices();
    const props: ExtendedFlexibleGalleryType = {
      removeTopSpacing: content.removeTopSpacing ?? false,
      variant: content.variant,
      componentHeadline: content.componentHeadline,
      galleryItems: content.items.map(
        (
          {
            contentType,
            imageUrl,
            videoLandscapeUrl,
            videoPortraitUrl,
            videoPosterImageUrl,
            mediaAltText,
            headline,
            headlineTag,
            copyText,
            link,
            itemId,
          },
          index,
        ) => ({
          contentType,
          image:
            contentType === `image`
              ? {
                  imageUrl: imageUrl || ``,
                  imageAltText: mediaAltText || ``,
                }
              : undefined,
          video:
            contentType === `video`
              ? {
                  landscapeUrl: videoLandscapeUrl || ``,
                  portraitUrl: videoPortraitUrl || ``,
                  posterImageUrl: videoPosterImageUrl || ``,
                  altText: mediaAltText || ``,
                }
              : undefined,
          headline,
          headlineTag: headlineTag as GalleryItem['headlineTag'],
          copyText,
          link: link
            ? createLinkProps({
                historyService,
                content: link,
                websiteTrackingData: {
                  type: `navigation`,
                  region: `gallery_v2`,
                  name: `${link.label}.${index}`,
                },
                envName: endpointDirectory.envName,
                loginState: loginService?.loginState,
                languageTag,
                marketId,
              })
            : undefined,
          itemId,
        }),
      ),
      legalDisclaimers: createLegalDisclaimerProps(
        content.legalDisclaimers,
        historyService,
      ),
      mode: content.modeSelector,
      playIconAriaLabel: pageModel?.translations?.playIconAriaLabel || ``,
      pauseIconAriaLabel: pageModel?.translations?.pauseIconAriaLabel || ``,
    };

    return <ExtendedFlexibleGallery {...props} />;
  },
  (prevProps, nextProps) => prevProps.content.id === nextProps.content.id,
);

ExtendedFlexibleGalleryWrapper.displayName = `ExtendedFlexibleGalleryWrapper`;
